/**
 * Dialogový systém (přístupný: focus trap, Esc, aria) + nastavení + onboarding.
 *
 * Nastavení je hierarchické (kategorie → podkategorie) s úrovněmi
 * komplexnosti; hodnoty se ukládají do globálních přepisů parametrů.
 * Citlivé parametry zůstávají jen lokálně.
 */
import { isParamId, PARAM_DEFS, type ParamDef } from '../core/params';
import {
  activeParams,
  buildShareUrl,
  exportJson,
  importJson,
  type Store,
} from '../state/store';
import { envOrsKey } from '../data/routing';
import { el } from './dom';
import { icon } from './icons';
import { renderParamEditor } from './paramsUi';

export function openDialog(opts: {
  title: string;
  body: HTMLElement;
  footer?: HTMLElement[];
  wide?: boolean;
  onClose?: () => void;
}): { close(): void } {
  const previouslyFocused = document.activeElement as HTMLElement | null;

  const dialog = el(
    'div',
    { className: `dialog${opts.wide ? ' dialog--wide' : ''}`, role: 'dialog', 'aria-modal': 'true', 'aria-label': opts.title },
    el(
      'div',
      { className: 'dialog__header' },
      el('span', { style: 'flex:1' }, opts.title),
      el('button', { className: 'btn btn--small btn--ghost', 'aria-label': 'Zavřít dialog', onclick: () => close() }, icon('close')),
    ),
    el('div', { className: 'dialog__body' }, opts.body),
    opts.footer ? el('div', { className: 'dialog__footer' }, ...opts.footer) : null,
  );

  const backdrop = el('div', { className: 'dialog-backdrop' }, dialog);
  backdrop.addEventListener('mousedown', (e) => {
    if (e.target === backdrop) close();
  });

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
    }
    if (e.key === 'Tab') {
      // jednoduchá focus past
      const focusables = dialog.querySelectorAll<HTMLElement>(
        'button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };
  document.addEventListener('keydown', onKey, true);

  function close(): void {
    document.removeEventListener('keydown', onKey, true);
    backdrop.remove();
    opts.onClose?.();
    previouslyFocused?.focus?.();
  }

  document.body.appendChild(backdrop);
  const firstInput = dialog.querySelector<HTMLElement>('input, select, button.btn--primary, button');
  firstInput?.focus();
  return { close };
}

/* ------------------------------------------------------------------ */
/* Nastavení                                                            */
/* ------------------------------------------------------------------ */

export function openSettings(store: Store, onChanged: () => void): void {
  /* ---- sekce Sdílení a záloha (vlastní záložka v navigaci) ---- */
  const importInput = el('input', {
    type: 'file',
    accept: 'application/json',
    style: 'display:none',
  }) as HTMLInputElement;
  const importStatus = el('div', { className: 'muted', role: 'status' });
  importInput.addEventListener('change', () => {
    const file = importInput.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const next = importJson(store.get(), String(reader.result));
      if (next) {
        store.update(next);
        onChanged();
        dlg.close();
      } else {
        importStatus.textContent = 'Soubor se nepodařilo načíst — není to platný export OptiRoute.';
      }
    };
    reader.readAsText(file);
  });

  const shareStatus = el('div', { className: 'muted', role: 'status' });

  const shareSection = el(
    'div',
    {},
    el(
      'div',
      { className: 'hint', style: 'margin-bottom:8px' },
      'Odkaz i export obsahují body, režimy, profily a nastavení. Vlastní endpointy a API klíče se nikdy nesdílejí. GTFS síť jde jen do exportu (odkaz by byl moc dlouhý).',
    ),
    el(
      'div',
      { className: 'row row--wrap' },
      el(
        'button',
        {
          className: 'btn',
          onclick: async () => {
            const url = buildShareUrl(store.get());
            try {
              await navigator.clipboard.writeText(url);
              shareStatus.textContent = 'Odkaz zkopírován (bez citlivých údajů a GTFS dat).';
            } catch {
              window.prompt('Zkopírujte odkaz ručně:', url);
            }
          },
        },
        icon('link'),
        ' Kopírovat odkaz',
      ),
      el(
        'button',
        {
          className: 'btn',
          onclick: () => {
            const blob = new Blob([exportJson(store.get())], { type: 'application/json' });
            const a = el('a', {
              href: URL.createObjectURL(blob),
              download: 'optiroute-konfigurace.json',
            });
            a.click();
            URL.revokeObjectURL(a.href);
          },
        },
        icon('download'),
        ' Export JSON',
      ),
      el('button', { className: 'btn', onclick: () => importInput.click() }, icon('upload'), ' Import JSON'),
      importInput,
    ),
    shareStatus,
    importStatus,
  );

  const editor = renderParamEditor({
    store,
    valueOf: (id) => activeParams(store.get()).get(id),
    isOverridden: (id) => store.get().paramValues[id] !== undefined,
    set: (id, value) => {
      store.update({ paramValues: { ...store.get().paramValues, [id]: value } });
      onChanged();
    },
    reset: (id) => {
      const pv = { ...store.get().paramValues };
      delete pv[id];
      store.update({ paramValues: pv });
      onChanged();
    },
    inheritedLabel: 'výchozí',
    extraSections: [{ name: 'Sdílení a záloha', icon: 'link', node: shareSection }],
  });

  const orsEnvNote = envOrsKey()
    ? el('div', { className: 'alert alert--info', style: 'margin-bottom:8px' }, 'OpenRouteService klíč je načten z env proměnné VITE_ORS_API_KEY.')
    : null;

  const body = el('div', {}, orsEnvNote, editor);

  const dlg = openDialog({
    title: 'Nastavení',
    body,
    wide: true,
    footer: [el('button', { className: 'btn btn--primary', onclick: () => dlg.close() }, 'Hotovo')],
  });
}

/** Krátký lidský popis parametru (pro jiná UI). */
export function paramLabel(id: string): string {
  return isParamId(id) ? (PARAM_DEFS[id] as ParamDef).label : id;
}

/* ------------------------------------------------------------------ */
/* Onboarding                                                            */
/* ------------------------------------------------------------------ */

export function openOnboarding(store: Store): void {
  const body = el(
    'div',
    { className: 'onboarding' },
    el('div', { className: 'onboarding__icon', 'aria-hidden': 'true' }, icon('route', { size: 40 })),
    el('h2', {}, 'Vítejte v OptiRoute'),
    el('p', {}, 'Porovnejte všechny smysluplné způsoby, jak se dostat z A do B — čas, cenu, čekání, přestupy, emise i komfort.'),
    el(
      'div',
      { className: 'onboarding__steps' },
      step(1, 'Zadejte start a cíl', 'Hledáním, kliknutím do mapy, souřadnicemi, nebo aktuální polohou. Markery jdou přetahovat.'),
      step(2, 'Vyberte, co porovnat', 'Pěšky, kolo, auto, MHD, brusle, kombinace… nebo vlastní režim. Profil (nahoře) přepne celé ladění najednou.'),
      step(3, 'Hrajte si s časem a mapou', 'Posuvník času ukáže, jak se výsledky mění během dne. „Zóny výhodnosti" vybarví mapu podle toho, odkud se co vyplatí.'),
    ),
    el(
      'p',
      { className: 'muted' },
      'Reálné trasy a adresy přicházejí z OpenStreetMap služeb; jízdní řády si nahrajete (GTFS), zadáte ručně, nebo zapnete označenou demo síť. Bez internetu běží poctivě označený odhadový model.',
    ),
  );

  const dlg = openDialog({
    title: 'Začínáme',
    body,
    footer: [
      el(
        'button',
        {
          className: 'btn btn--primary',
          onclick: () => {
            store.update({ onboarded: true });
            dlg.close();
          },
        },
        'Rozumím, jdeme na to',
      ),
    ],
    onClose: () => store.update({ onboarded: true }),
  });

  function step(n: number, title: string, text: string): HTMLElement {
    return el(
      'div',
      { className: 'onboarding__step' },
      el('div', { className: 'onboarding__num', 'aria-hidden': 'true' }, String(n)),
      el('div', {}, el('strong', {}, title), el('span', {}, text)),
    );
  }
}
